import React, { useState, useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { LazyImage } from '../utils/performance-optimizations';
import AacButton from '../components/aac/AacButton';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const Map = () => {
  const { announce, focusFirstHeading } = useAccessibility();
  useFocusableNavigation();
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Campus locations and venue information
  const venues = [
    {
      id: 'esther-simpson-lg08',
      name: 'Esther Simpson, LG08',
      description: 'Large conference venue for keynote presentations and main sessions',
      capacity: '390 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Esther Simpson Building, Lower Ground Floor'
    },
    {
      id: 'michael-sadler-lg10',
      name: 'Michael Sadler, LG10',
      description: 'Medium-sized venue for workshops and presentations',
      capacity: '80 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Michael Sadler Building, Lower Ground Floor'
    },
    {
      id: 'michael-sadler-lg15',
      name: 'Michael Sadler, LG15',
      description: 'Seminar room for smaller group sessions and workshops',
      capacity: '59 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Michael Sadler Building, Lower Ground Floor'
    },
    {
      id: 'michael-sadler-lg17',
      name: 'Michael Sadler, LG17',
      description: 'Intimate meeting room for small group discussions',
      capacity: '26 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Michael Sadler Building, Lower Ground Floor'
    },
    {
      id: 'michael-sadler-lg19',
      name: 'Michael Sadler, LG19',
      description: 'Seminar room for workshops and breakout sessions',
      capacity: '67 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Michael Sadler Building, Lower Ground Floor'
    },
    {
      id: 'clothworkers-south-g11ab',
      name: 'Clothworkers South, G11a&b',
      description: 'Combined seminar rooms for larger workshops and presentations',
      capacity: '90 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Clothworkers South Building, Ground Floor'
    },
    {
      id: 'clothworkers-south-g14',
      name: 'Clothworkers South, G14',
      description: 'Seminar room for workshops and group sessions',
      capacity: '60 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Clothworkers South Building, Ground Floor'
    },
    {
      id: 'stage-leeds-stage1',
      name: 'Stage@Leeds, Stage 1',
      description: 'Performance and presentation space with theatre-style seating',
      capacity: '180 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Stage@Leeds Building'
    },
    {
      id: 'stage-leeds-dance-studio',
      name: 'Stage@Leeds, Dance Studio',
      description: 'Flexible studio space for interactive workshops and movement sessions',
      capacity: '50 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Stage@Leeds Building'
    },
    {
      id: 'stage-leeds-alec-clegg',
      name: 'Stage@Leeds, Alec Clegg Studio',
      description: 'Studio space for creative workshops and presentations',
      capacity: '52 people',
      accessibility: 'Wheelchair accessible, free Wi-Fi available',
      facilities: ['PC/laptop connection', 'Large screen', 'USB port', 'Amplified speakers', 'Wi-Fi', 'Bluetooth speaker for AAC users'],
      location: 'Stage@Leeds Building'
    }
  ];

  // Conference venues loaded

  const accessibilityFeatures = [
    {
      icon: '♿',
      title: 'Wheelchair Access',
      description: 'All venues are wheelchair accessible with ramps and lifts available'
    },
    {
      icon: '🔊',
      title: 'Hearing Support',
      description: 'Hearing loops in main venues, portable loops available for smaller rooms'
    },
    {
      icon: '🚻',
      title: 'Accessible Toilets',
      description: 'Accessible toilet facilities on every floor'
    },
    {
      icon: '🅿️',
      title: 'Accessible Parking',
      description: 'Designated accessible parking spaces near main entrances'
    },
    {
      icon: '🧭',
      title: 'Clear Signage',
      description: 'Large print, high contrast signage with symbols throughout the venue'
    },
    {
      icon: '😌',
      title: 'Quiet Spaces',
      description: 'Dedicated quiet rooms for sensory breaks and decompression'
    }
  ];

  useEffect(() => {
    focusFirstHeading();
    announce('Venue map and information page loaded');
  }, [announce, focusFirstHeading]);

  const selectLocation = (venue) => {
    setSelectedLocation(venue);
    announce(`Selected ${venue.name}`);
  };

  const downloadMap = () => {
    // Download the PDF map file
    const link = document.createElement('a');
    link.href = '/CM2026_Campus_Map.pdf';
    link.download = 'CM2026_Campus_Map.pdf';
    link.click();
    announce('Campus map download started');
  };

  // Component render
  // Map component rendering
  return (
    <div className="map-page">
      <div className="container">
        <header className="page-header">
          <h1>Venue Map & Information</h1>
          <p className="page-description">
            Find your way around the conference venue with accessibility information and venue details.
          </p>
        </header>

        {/* Quick Actions */}
        <section className="quick-actions" aria-labelledby="actions-title">
          <h2 id="actions-title" className="sr-only">Quick Actions</h2>
          <div className="actions-grid">
            <AacButton onClick={downloadMap} shortcut={1}>
              📄 Download Map
            </AacButton>

            <a href="#accessibility-info" className="action-card">
              <span className="action-icon" aria-hidden="true">♿</span>
              <span className="action-title">Accessibility Info</span>
              <span className="action-description">View accessibility features</span>
            </a>

            <a href="#venue-list" className="action-card">
              <span className="action-icon" aria-hidden="true">📍</span>
              <span className="action-title">Venue Details</span>
              <span className="action-description">Browse all conference venues</span>
            </a>
          </div>
        </section>

        {/* Campus Map */}
        <section className="map-section" aria-labelledby="map-title">
          <h2 id="map-title">Campus Map</h2>
          <p className="map-description">
            The Communication Matters 2026 conference is hosted across multiple buildings at the University of Leeds campus.
            All venues are wheelchair accessible with dedicated parking, accessible entrances, and comprehensive facilities.
          </p>

          <div className="map-container">
            <div className="map-wrapper">
              <LazyImage
                src="/campus-map-clean.png"
                alt="University of Leeds Campus Map (extracted from PDF) showing Communication Matters 2026 conference venues and accessibility features"
                className="campus-map"
                priority={true}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
              />
            </div>
            

            

          </div>

          <div className="map-legend">
            <h3>Map Legend</h3>
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#FFF275' }} />
                <span>Conference Venues</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#4CAF50' }} />
                <span>Accessible Entrances</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#2196F3' }} />
                <span>Parking Areas</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#FF9800' }} />
                <span>Catering & Facilities</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#9C27B0' }} />
                <span>Information Points</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#F44336' }} />
                <span>Emergency Assembly</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#607D8B' }} />
                <span>Accessible Toilets</span>
              </div>
            </div>
          </div>
        </section>

        {/* Accessibility Information */}
        <section id="accessibility-info" className="accessibility-section" aria-labelledby="accessibility-title">
          <h2 id="accessibility-title">Accessibility Features</h2>
          <p className="section-description">
            The venue is designed to be fully accessible. Here are the key accessibility features available:
          </p>

          <div className="accessibility-grid">
            {accessibilityFeatures.map((feature, index) => (
              <div key={`accessibility-${index}`} className="accessibility-card">
                <div className="feature-icon" aria-hidden="true">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="accessibility-contact">
            <h3>Need Additional Support?</h3>
            <p>
              If you have specific accessibility requirements not covered above,
              please contact the conference organizers in advance.
            </p>
            <div className="contact-info">
              <strong>Accessibility Coordinator:</strong> Available on request
            </div>
          </div>
        </section>



        {/* Venue List */}
        <section id="venue-list" className="venues-section" aria-labelledby="venues-title">
          <h2 id="venues-title">Conference Venues</h2>
          <p className="section-description">
            Detailed information about each conference venue, including capacity and accessibility features.
          </p>

          {/* General Venue Information */}
          <div className="venue-general-info">
            <div className="info-card accessibility-info">
              <h3>♿ Accessibility</h3>
              <p>All rooms are wheelchair accessible, and free Wi-Fi is available throughout.</p>
            </div>
            
            <div className="info-card presenter-info">
              <h3>🎤 For Presenters</h3>
              <p>All rooms have PC/laptop connection, large screen, USB port, connection to amplified speakers and Wi-Fi as standard. There will also be a Bluetooth speaker in every session room for AAC users who are presenting.</p>
            </div>
          </div>

          <div className="venues-grid">
            {venues.map((venue) => (
              <article
                key={venue.id}
                className={`venue-card ${selectedLocation?.id === venue.id ? 'selected' : ''}`}
                onClick={() => selectLocation(venue)}
                tabIndex="0"
                role="button"
                aria-pressed={selectedLocation?.id === venue.id}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectLocation(venue);
                  }
                }}
              >
                <header className="venue-header">
                  <h3 className="venue-name">{venue.name}</h3>
                  <span className="venue-location">{venue.location}</span>
                </header>

                <div className="venue-details">
                  <p className="venue-description">{venue.description}</p>

                  <div className="venue-info">
                    <div className="info-item">
                      <strong>Capacity:</strong> {venue.capacity}
                    </div>
                    <div className="info-item">
                      <strong>Accessibility:</strong> {venue.accessibility}
                    </div>
                  </div>

                  <div className="venue-facilities">
                    <strong>Facilities:</strong>
                    <ul className="facilities-list">
                      {venue.facilities.map((facility, index) => (
                        <li key={`facility-${index}`}>{facility}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Selected Venue Details */}
        {selectedLocation && (
          <section className="selected-venue" aria-labelledby="selected-title">
            <h2 id="selected-title">Selected Venue: {selectedLocation.name}</h2>

            <div className="selected-details">
              <div className="selected-info">
                <h3>Venue Information</h3>
                <p><strong>Location:</strong> {selectedLocation.location}</p>
                <p><strong>Capacity:</strong> {selectedLocation.capacity}</p>
                <p><strong>Description:</strong> {selectedLocation.description}</p>
              </div>

              <div className="selected-accessibility">
                <h3>Accessibility Features</h3>
                <p>{selectedLocation.accessibility}</p>

                <h4>Available Facilities</h4>
                <ul>
                  {selectedLocation.facilities.map((facility, index) => (
                    <li key={`selected-facility-${index}`}>{facility}</li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              onClick={() => setSelectedLocation(null)}
              className="btn btn-outline"
              aria-label="Close venue details"
            >
              Close Details
            </button>
          </section>
        )}
      </div>

    </div>
  );
};

export default Map;
